import type { FailedPage } from '@/models/Orgs'
import { getBlockedUrlMessage } from '@/utils/urlValidation'

/** Why the site, not our stack, stopped us reading a page. */
export type CrawlFailureKind =
  | 'blocked'
  | 'notFound'
  | 'noText'
  | 'unreachable'
  | 'tooLarge'
  | 'notUseful'

/**
 * Matches the fixed strings the backend sends for a crawler failure
 * (`CRAWL_FAILURE_ERRORS` and `NO_USEFUL_CONTENT_ERROR` in
 * `app/utils/file_upload.py`). Returns null for anything else.
 */
export function crawlFailureKind(error: string | undefined): CrawlFailureKind | null {
  if (!error) return null
  if (error.startsWith('Website blocked access')) return 'blocked'
  if (error.startsWith('Website page not found')) return 'notFound'
  if (error.startsWith('No readable text found')) return 'noText'
  if (error.startsWith('Failed to reach website')) return 'unreachable'
  if (error.startsWith('Website page too large')) return 'tooLarge'
  if (error.startsWith('No useful content found')) return 'notUseful'
  // Older results, still held in saved wizard data, sent one string for both an
  // empty page and one judged not useful. The hedged no-text copy fits both.
  if (error.includes('Failed to extract')) return 'noText'
  return null
}

export const PDF_ADVICE = 'Save the page as a PDF and upload it as a document instead.'

const CRAWL_FAILURE_MESSAGES: Record<CrawlFailureKind, string> = {
  blocked: `This website blocked us from reading it. ${PDF_ADVICE}`,
  notFound: 'We couldn’t find this page. Please check the address is correct.',
  noText:
    'We couldn’t read any text on this page. It may be that this site uses ' +
    'protections that stop us reading it, or builds its content in a way we ' +
    `can’t read. ${PDF_ADVICE}`,
  unreachable:
    'We couldn’t reach this website. Please try again later, or save the page ' +
    'as a PDF and upload it as a document.',
  tooLarge: `This page is too large for us to read. ${PDF_ADVICE}`,
  notUseful: 'We couldn’t find any useful information on this page.',
}

/**
 * Maps a raw per-website failure string to the curated, user-facing message the
 * website upload components show for a single failed website. Used for the
 * single-failure case in `applyUploadResults`, and reused for whole-job failures
 * (SSE `onError`, the initial upload request's `catch`) so those paths go through
 * the same safe/unsafe filter instead of rendering raw backend text. Anything
 * that doesn't match one of these known-safe patterns — including raw internal
 * exception text from a whole-job failure — falls back to `fallback`.
 */
export function formatWebsiteUploadError(
  error: string | undefined,
  fallback: string,
): string {
  if (!error) return fallback
  // A blocked host is valid and reachable — it just can't be read — so it must
  // not be reported as an invalid or unreachable URL.
  if (error.includes('Website URL not supported')) {
    return getBlockedUrlMessage()
  }
  if (error.includes('Website URL not allowed')) {
    return `${error} - Please make sure the URL is valid and publically accessible.`
  }
  if (error.includes('has already been uploaded')) {
    return `${error} - Please delete the website from the Uploaded list first.`
  }
  const kind = crawlFailureKind(error)
  if (kind) return CRAWL_FAILURE_MESSAGES[kind]
  return fallback
}

export const FAILED_PAGE_FALLBACK = 'Sorry! Error uploading website - Please try again.'

/**
 * One line of the "several websites failed" summary, reusing the exact same
 * safe/unsafe matcher as the single-failure case above so the two can't diverge
 * again (this used to be a second, separately hand-rolled copy of the same
 * prefix-matching in each website component).
 */
export function formatWebsiteFailureLine(
  website: string,
  error: string | undefined,
): string {
  return `• ${website}: ${formatWebsiteUploadError(error, FAILED_PAGE_FALLBACK)}`
}

export const REFETCH_FALLBACK =
  "Sorry, we couldn't get the latest content. Please try again."

/**
 * A refetch the crawler could not complete still answers 200, with
 * `success: false`, so the caller must check the result as well as the request.
 */
export function refetchFailureMessage(result: {
  success: boolean
  error?: string | null
}): string | null {
  if (result.success) return null
  return formatWebsiteUploadError(result.error ?? undefined, REFETCH_FALLBACK)
}

export const NO_REFETCH_ISSUES: { failedPages: FailedPage[]; warnings: string[] } = {
  failedPages: [],
  warnings: [],
}
